import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    X,
    Save,
    Loader2,
    Check,
    MoreVertical,
    MessageSquare,
    Eye,
    PanelLeft,
    FileText,
    Users,
    Code,
    History,
    Globe,
    Lock,
    Database,
    Puzzle,
    Key,
    Settings,
    Plug,
    Plus,
    ExternalLink,
} from "lucide-react";
import { useResponsive, trackWorkflowUsage } from "@utils";
import { useLayout } from "../../context/LayoutContext";
import ChatMainInput from "../components/ChatMainInput";
import ChatMessages from "../ChatMessages";
import RightPanel from "./RightPanel";
import { useChatContext } from "../ChatContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { ResizablePanels, Panel } from "../../components/ResizablePanels";
import AppV2Header from "../components/AppV2Header";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import PageSelector from "../components/PageSelector";
import { Bluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import ChatPageSkeleton from "./ChatPageSkeleton";
import ChatPageSkeletonMobile from "./ChatPageSkeletonMobile";
import useWorkflowPreviewToken from "../../hooks/useWorkflowPreviewToken";
import ChatHistoryPanel from "./ChatHistoryPanel";

const panelLayout = {
    "left-panel": { width: 30 },
    "right-panel": { width: 70 },
};

const PANEL_STYE = {};

export default function ChatTwoColumnPage({ confirm }) {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { toggleSidebar } = useLayout();
    const {
        pendingMessage,
        pendingFiles,
        uploadFiles,
        setPendingFiles,
        setPendingMessage,
        stagedFiles,
        clearStagedFiles,
        navigateToOverview,
        navigateToUsers,
        navigateToEditor,
        navigateToVersionHistory,
        navigateToDomains,
        navigateToAuthentication,
        navigateToDataSecurity,
        navigateToIntegrations,
        navigateToSecrets,
        navigateToSettings,
        navigateToAPIs,
        setAppViewMode,
        appViewMode,
        files: uploaderFiles,
        prefillMessage,
        setPrefillMessage,
    } = useChatContext();
    const { workflow, workflowId, updateWorkflow, isLoading } = useWorkflow();
    const {
        handleChat,
        isGeneratingCode,
        stepChatHistory,
        selectedStepChatId,
        selectChatSession,
        startNewChat,
        lastGenerationModifiedFiles,
        fetchChatHistory,
        getStepChatHistoryLoading,
        loadMoreChatHistory,
        hasMoreChatHistory,
        isLoadingMoreChatHistory,
    } = useWorkflowGeneration();
    const refIsGeneratingCode = useRef(isGeneratingCode);

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const sendedChat = useRef(false);

    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

    const handleEditClick = () => {
        setEditValue(workflow?.name || "");
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (isUpdating) return;
        const trimmed = editValue.trim();
        if (!trimmed || trimmed === workflow?.name) {
            setIsEditing(false);
            return;
        }
        try {
            setIsUpdating(true);
            await updateWorkflow({ name: trimmed });
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating workflow name:", error);
            const reason =
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                error?.message ||
                t("chatTwoColumnPage.renameErrorUnknown", {
                    defaultValue: "an unexpected error occurred",
                });
            toast.error(
                t("chatTwoColumnPage.renameError", {
                    reason,
                    defaultValue:
                        "Couldn't rename workflow to \"{{name}}\": {{reason}}. Your change wasn't saved — try again.",
                    name: trimmed,
                })
            );
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditValue("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    // Long-lived (7-day) workflow-scoped JWT for the detached new-tab session.
    // Replaces putting the platform Bearer in the URL as x_token_auth.
    const { issueLongLivedToken } = useWorkflowPreviewToken(workflow?.id);

    // Handle open app in new tab with authentication
    const handleOpenApp = async () => {
        if (!workflow?.internalDomain) return;

        try {
            const currentVersion = workflow?.steps?.[0]?.currentVersion;

            const { componentServerUrl: basePath } =
                Bluelagoon.getComponentServerUrl(workflow.internalDomain);
            const token = await issueLongLivedToken();
            if (!token) {
                toast.error(
                    t("chatTwoColumnPage.openAppTokenError", {
                        defaultValue:
                            "Couldn't open the app: failed to issue a secure access token. Try again in a moment.",
                    })
                );
                return;
            }
            const params = new URLSearchParams({
                v: String(currentVersion || Date.now()),
                x_deep_link_token: token,
            });
            const opened = window.open(
                `${basePath}?${params.toString()}`,
                "_blank",
                "noopener,noreferrer"
            );
            if (!opened) {
                toast.error(
                    t("chatTwoColumnPage.openAppPopupBlocked", {
                        defaultValue:
                            "Couldn't open a new tab — your browser may be blocking pop-ups. Allow pop-ups for this site and try again.",
                    })
                );
            }
        } catch (error) {
            console.error("Error opening app in new tab:", error);
            const reason =
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                error?.message ||
                t("chatTwoColumnPage.openAppErrorUnknown", {
                    defaultValue: "an unexpected error occurred",
                });
            toast.error(
                t("chatTwoColumnPage.openAppError", {
                    reason,
                    defaultValue:
                        "Couldn't open the app in a new tab: {{reason}}. Try again.",
                })
            );
        }
    };

    useEffect(() => {
        // Track workflow usage when page is accessed
        if (workflow?.id) {
            trackWorkflowUsage(workflow.id);
        }
    }, [workflow?.id]);

    // Track if we're waiting for files to upload before sending message
    const waitingForFilesRef = useRef(false);
    const pendingMessageRef = useRef(null);
    const expectedFileCountRef = useRef(0);
    const uploadTimeoutRef = useRef(null);
    const uploadStartTimeRef = useRef(null);
    const stagedFilesBeforeUploadRef = useRef(0);
    // useUploader appends to its file list and never clears, so we snapshot
    // the length before each batch to isolate this batch's files from prior
    // (possibly errored) entries that would otherwise poison error counts.
    const uploaderFilesBeforeUploadRef = useRef(0);
    // Ref to track current staged files for timeout callback
    const stagedFilesRef = useRef([]);

    // Keep stagedFilesRef in sync with stagedFiles
    useEffect(() => {
        stagedFilesRef.current = stagedFiles;
    }, [stagedFiles]);

    // File upload timeout duration (30 seconds)
    const FILE_UPLOAD_TIMEOUT_MS = 30000;

    // Handle pending files - upload them first
    useEffect(() => {
        if (pendingFiles.length > 0 && workflow?.id && workflowId) {
            // Store the pending message for later
            if (pendingMessage) {
                pendingMessageRef.current = pendingMessage;
                waitingForFilesRef.current = true;
                setPendingMessage(null);
            }
            // Track how many files we expect to be staged
            const expectedCount = pendingFiles.length;
            expectedFileCountRef.current = expectedCount;
            // Track the current staged files count before upload
            const beforeCount = stagedFiles.length;
            stagedFilesBeforeUploadRef.current = beforeCount;
            uploaderFilesBeforeUploadRef.current = uploaderFiles.length;
            // Track upload start time
            uploadStartTimeRef.current = Date.now();

            // Set a timeout for upload failure recovery
            if (uploadTimeoutRef.current) {
                clearTimeout(uploadTimeoutRef.current);
            }
            uploadTimeoutRef.current = setTimeout(() => {
                if (
                    waitingForFilesRef.current &&
                    pendingMessageRef.current &&
                    !sendedChat.current
                ) {
                    console.warn(
                        "File upload timeout - sending message with available files"
                    );
                    const messageToSend = pendingMessageRef.current;
                    // Use ref to get current staged files at timeout time
                    const filesToSend = [...stagedFilesRef.current];

                    // Calculate before resetting refs
                    const uploadedCount = filesToSend.length - beforeCount;
                    const someFilesFailed = uploadedCount < expectedCount;

                    // Reset refs
                    waitingForFilesRef.current = false;
                    pendingMessageRef.current = null;
                    expectedFileCountRef.current = 0;
                    uploadStartTimeRef.current = null;
                    stagedFilesBeforeUploadRef.current = 0;
                    uploaderFilesBeforeUploadRef.current = 0;
                    sendedChat.current = true;

                    // Notify user about timeout
                    if (someFilesFailed) {
                        toast.error(
                            t(
                                "chatTwoColumnPage.fileUploadTimeout",
                                "Some files could not be uploaded. Message sent with available files."
                            )
                        );
                    }

                    // Send with whatever files we have
                    handleChat(messageToSend, [], filesToSend);
                    clearStagedFiles();
                }
            }, FILE_UPLOAD_TIMEOUT_MS);

            // Upload files
            uploadFiles(pendingFiles);
            setPendingFiles([]);
        }
    }, [
        pendingFiles,
        uploadFiles,
        workflow?.id,
        workflowId,
        setPendingFiles,
        pendingMessage,
        setPendingMessage,
        stagedFiles,
        handleChat,
        clearStagedFiles,
        t,
    ]);

    // Clear the upload timeout only on unmount. Keeping this in a separate
    // effect with [] deps prevents it from being torn down on every re-render
    // of the staging effect above (which would defeat the 30s safety-net
    // recovery for silently-stalled uploads).
    useEffect(() => {
        return () => {
            if (uploadTimeoutRef.current) {
                clearTimeout(uploadTimeoutRef.current);
            }
        };
    }, []);

    // Handle pending message - send after files are uploaded (success or failure)
    useEffect(() => {
        // If we're waiting for files, check if uploads are complete
        if (
            waitingForFilesRef.current &&
            expectedFileCountRef.current > 0 &&
            pendingMessageRef.current &&
            !sendedChat.current
        ) {
            // Count how many of our expected files have completed (either successfully staged or failed)
            const newStagedCount =
                stagedFiles.length - stagedFilesBeforeUploadRef.current;

            // Slice from the pre-upload snapshot — anything earlier is a
            // leftover from a prior batch and must not count toward this send.
            const recentUploads = uploaderFiles.slice(
                uploaderFilesBeforeUploadRef.current
            );

            const uploadingCount = recentUploads.filter(
                (f) => f.status === "uploading"
            ).length;
            const completedCount = recentUploads.filter(
                (f) => f.status === "completed"
            ).length;
            const errorCount = recentUploads.filter(
                (f) => f.status === "error"
            ).length;

            // All uploads are done when either:
            // 1. We have all expected files staged (success case)
            // 2. All tracked uploads are complete (success + error = expected) and no files are still uploading
            const allUploadsComplete =
                uploadingCount === 0 &&
                completedCount + errorCount >= expectedFileCountRef.current;
            const allFilesStaged =
                newStagedCount >= expectedFileCountRef.current;

            if (allFilesStaged || allUploadsComplete) {
                // Clear the timeout since we're handling it now
                if (uploadTimeoutRef.current) {
                    clearTimeout(uploadTimeoutRef.current);
                    uploadTimeoutRef.current = null;
                }

                const messageToSend = pendingMessageRef.current;
                const filesToSend = [...stagedFiles];

                // Reset refs
                waitingForFilesRef.current = false;
                pendingMessageRef.current = null;
                const expectedCount = expectedFileCountRef.current;
                expectedFileCountRef.current = 0;
                uploadStartTimeRef.current = null;
                stagedFilesBeforeUploadRef.current = 0;
                uploaderFilesBeforeUploadRef.current = 0;
                sendedChat.current = true;

                // Notify user if some files failed
                if (errorCount > 0) {
                    toast.error(
                        t("chatTwoColumnPage.fileUploadFailed", {
                            count: errorCount,
                            defaultValue: `${errorCount} file(s) failed to upload. Message sent with available files.`,
                        })
                    );
                }

                // Send with staged files
                handleChat(messageToSend, [], filesToSend);
                clearStagedFiles();
            }
        }
        // If we have a pending message but no pending files, send immediately
        else if (
            pendingMessage &&
            !sendedChat.current &&
            workflow?.id &&
            pendingFiles.length === 0 &&
            !waitingForFilesRef.current
        ) {
            handleChat(pendingMessage, [], stagedFiles);
            sendedChat.current = true;
            setPendingMessage(null);
            if (stagedFiles.length > 0) {
                clearStagedFiles();
            }
        }
    }, [
        pendingMessage,
        stagedFiles,
        handleChat,
        workflow?.id,
        setPendingMessage,
        clearStagedFiles,
        pendingFiles.length,
        uploaderFiles,
        t,
    ]);

    React.useEffect(() => {
        // Only switch to preview when generation completes AND files were actually modified
        // This prevents switching when AI just chatted without making changes
        if (
            !isGeneratingCode &&
            refIsGeneratingCode.current &&
            lastGenerationModifiedFiles
        ) {
            setAppViewMode("preview");
        }
        refIsGeneratingCode.current = isGeneratingCode;
    }, [isGeneratingCode, lastGenerationModifiedFiles]);

    if (isLoading && !workflow) {
        return isMobile ? <ChatPageSkeletonMobile /> : <ChatPageSkeleton />;
    }

    if (isMobile) {
        return (
            <div className="bg-white flex flex-col relative h-screen overflow-hidden">
                {/* Top Bar with Workflow Name */}
                <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 bg-white flex items-center gap-3">
                    {/* Hamburger Menu Button */}
                    <button
                        onClick={toggleSidebar}
                        className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                        aria-label={t("common.toggleSidebar")}
                    >
                        <PanelLeft className="w-5 h-5 text-gray-700" />
                    </button>

                    {/* Workflow Name Section */}
                    <div className="flex-1 min-w-0 flex items-center justify-between">
                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) =>
                                        setEditValue(e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    onBlur={handleSave}
                                    className="flex-1 px-2 py-1 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-gray-900"
                                    autoFocus
                                    disabled={isUpdating}
                                />
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={handleSave}
                                    disabled={isUpdating}
                                    className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded disabled:opacity-50 flex-shrink-0"
                                >
                                    {isUpdating ? (
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <Check size={18} />
                                    )}
                                </button>
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={handleCancel}
                                    disabled={isUpdating}
                                    className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50 flex-shrink-0"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between w-full">
                                <div
                                    className="cursor-pointer truncate flex-1 min-w-0"
                                    onClick={handleEditClick}
                                >
                                    <h1 className="text-sm font-semibold text-gray-900 truncate">
                                        {workflow?.name ||
                                            t(
                                                "chatTwoColumnPage.untitledWorkflow"
                                            )}
                                    </h1>
                                </div>
                                {appViewMode === "chat" ? (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={startNewChat}
                                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden relative">
                    {/* Chat View */}
                    <div
                        className={`absolute inset-0 flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden ${
                            appViewMode === "chat"
                                ? "opacity-100 translate-x-0 pointer-events-auto"
                                : "opacity-0 -translate-x-4 pointer-events-none"
                        }`}
                    >
                        {/* Messages Section */}
                        <div className="flex-1 overflow-hidden min-w-0">
                            <ChatMessages />
                        </div>

                        {/* Input Section */}
                        <div className="p-4 border-t border-gray-100 flex-shrink-0">
                            {workflow?.status === "ARCHIVED" && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-yellow-800">
                                        <svg
                                            className="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <span className="text-sm font-medium">
                                            {t(
                                                "chatTwoColumnPage.workflowArchived"
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <ChatMainInput initialMessage={prefillMessage} onMessageSet={() => setPrefillMessage("")} />
                        </div>
                    </div>

                    {/* Preview View */}
                    <div
                        className={`absolute inset-0 flex flex-col h-full transition-all duration-300 ease-in-out ${
                            appViewMode === "preview" ||
                            appViewMode === "dashboard"
                                ? "opacity-100 translate-x-0 pointer-events-auto"
                                : "opacity-0 translate-x-4 pointer-events-none"
                        }`}
                    >
                        <RightPanel confirm={confirm} />
                    </div>
                </div>

                {/* Bottom Navigation Bar */}
                <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0 safe-area-inset-bottom ">
                    {/* Page Selector - shown when preview is selected */}
                    {appViewMode === "preview" ? (
                        <div className="mb-3">
                            <PageSelector />
                        </div>
                    ) : null}

                    <div className="flex items-center gap-3">
                        {/* Segmented Toggle Control for Chat/Preview */}
                        <div className="flex-1 bg-gray-100 rounded-lg p-1 flex gap-1">
                            <button
                                onClick={() => setAppViewMode("chat")}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all duration-200 ${
                                    appViewMode === "chat"
                                        ? "bg-white shadow-sm text-blue-600 font-medium"
                                        : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                                <MessageSquare className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                    {t("chatTwoColumnPage.chat")}
                                </span>
                            </button>

                            <button
                                onClick={() => setAppViewMode("preview")}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all duration-200 ${
                                    appViewMode === "preview"
                                        ? "bg-white shadow-sm text-blue-600 font-medium"
                                        : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                                <Eye className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                    {t("chatTwoColumnPage.preview")}
                                </span>
                            </button>
                        </div>

                        {workflow?.workflowType === "APP_V2" ? (
                            <button
                                onClick={() => setIsBottomSheetOpen(true)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2.5 rounded-lg transition-colors"
                                aria-label={t("chatTwoColumnPage.openMenu")}
                            >
                                <MoreVertical className="w-5 h-5" />
                            </button>
                        ) : null}
                    </div>
                </div>

                {/* Bottom Sheet Menu */}
                {isBottomSheetOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden animate-fadeIn">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-200"
                            onClick={() => setIsBottomSheetOpen(false)}
                        />

                        {/* Bottom Sheet */}
                        <div
                            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slideUpFade"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Handle Bar */}
                            <div className="flex justify-center pt-3 pb-2">
                                <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                            </div>

                            {/* Menu Header */}
                            <div className="px-4 py-3 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {t("appPreview.dashboardMenu")}
                                </h3>
                            </div>

                            {/* Menu Items */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="space-y-1">
                                    {workflow?.internalDomain ? (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsBottomSheetOpen(false);
                                                handleOpenApp();
                                            }}
                                            className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3 cursor-pointer"
                                        >
                                            <ExternalLink className="w-5 h-5" />
                                            <span className="font-medium">
                                                {t("appPreview.openInNewTab")}
                                            </span>
                                        </button>
                                    ) : null}
                                    {/* Overview - Opens in New Tab */}
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToOverview();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <FileText className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.overview")}
                                        </span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToUsers();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Users className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.users")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToEditor();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Code className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.editor")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToVersionHistory();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <History className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.versionHistory")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToDomains();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Globe className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.domains")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToAuthentication();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Lock className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.authentication")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToDataSecurity();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Database className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.dataSecurity")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToIntegrations();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Puzzle className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.integrations")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToSecrets();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Key className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.secrets")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToSettings();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Settings className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.settings")}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsBottomSheetOpen(false);
                                            navigateToAPIs();
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-3"
                                    >
                                        <Plug className="w-5 h-5" />
                                        <span className="font-medium">
                                            {t("appPreview.apis")}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-white flex flex-col lg:flex-row relative h-full">
            {/* Left Column - Chat Interface (Desktop) */}

            <ResizablePanels
                layoutId="chat-layout"
                defaultLayout={panelLayout}
                className="h-full"
                style={PANEL_STYE}
            >
                <Panel panelId="left-panel" className="h-full min-w-0 overflow-hidden">
                    <div className="flex flex-col h-full sticky top-0 relative overflow-hidden min-w-0">
                        {/* Messages Section */}
                        <div className="flex items-center">
                            <div className="flex items-center w-full px-4 py-2 border-b border-gray-200 h-12">
                                <button
                                    onClick={toggleSidebar}
                                    className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                                    aria-label={t("common.toggleSidebar")}
                                >
                                    <PanelLeft className="w-5 h-5 text-gray-700" />
                                </button>

                                {isEditing ? (
                                    <div className="">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) =>
                                                setEditValue(e.target.value)
                                            }
                                            onKeyDown={handleKeyDown}
                                            onBlur={handleSave}
                                            className="px-2 py-1 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent text-gray-900 "
                                            autoFocus
                                            disabled={isUpdating}
                                        />
                                        <button
                                            onMouseDown={(e) =>
                                                e.preventDefault()
                                            }
                                            onClick={handleSave}
                                            disabled={isUpdating}
                                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded disabled:opacity-50"
                                        >
                                            {isUpdating ? (
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                            ) : (
                                                <Check size={16} />
                                            )}
                                        </button>
                                        <button
                                            onMouseDown={(e) =>
                                                e.preventDefault()
                                            }
                                            onClick={handleCancel}
                                            disabled={isUpdating}
                                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className="group cursor-pointer flex-1 min-w-0"
                                        onClick={handleEditClick}
                                    >
                                        <span className="text-sm font-semibold text-gray-900 group-hover:bg-gray-100 group-hover:bg-opacity-50 px-2 py-1 rounded transition-all duration-200 truncate block">
                                            {workflow?.name ||
                                                t(
                                                    "chatTwoColumnPage.untitledWorkflow"
                                                )}
                                        </span>
                                    </div>
                                )}

                                <div className="ml-auto flex items-center gap-1">
                                    <button
                                        onClick={() => {
                                            setShowHistory(false);
                                            startNewChat();
                                        }}
                                        className=" p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                        title={t("chatTwoColumnPage.newChat")}
                                    >
                                        <Plus className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setShowHistory(!showHistory)
                                        }
                                        className={`p-2 rounded-lg transition-colors ${
                                            showHistory
                                                ? "text-blue-600 bg-blue-50"
                                                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                        }`}
                                        title={t(
                                            "chatTwoColumnPage.pastConversationsTitle"
                                        )}
                                    >
                                        <History className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <ChatHistoryPanel
                            isOpen={showHistory}
                            onClose={() => setShowHistory(false)}
                            chats={stepChatHistory}
                            selectedStepChatId={selectedStepChatId}
                            onSelect={selectChatSession}
                            onRefresh={fetchChatHistory}
                            isLoading={getStepChatHistoryLoading}
                            hasMore={hasMoreChatHistory}
                            onLoadMore={loadMoreChatHistory}
                            isLoadingMore={isLoadingMoreChatHistory}
                        />
                        <div className="flex-1 overflow-hidden min-w-0">
                            <ChatMessages />
                        </div>

                        {/* Input Section */}
                        <div className="p-4 border-t border-gray-100 flex-shrink-0">
                            {workflow?.status === "ARCHIVED" && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-yellow-800">
                                        <svg
                                            className="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <span className="text-sm font-medium">
                                            {t(
                                                "chatTwoColumnPage.workflowArchived"
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <ChatMainInput initialMessage={prefillMessage} onMessageSet={() => setPrefillMessage("")} />
                        </div>
                    </div>
                </Panel>
                <Panel
                    panelId="right-panel"
                    className="w-full flex flex-col h-screen"
                >
                    <AppV2Header />
                    <RightPanel confirm={confirm} />
                </Panel>
            </ResizablePanels>
            {/* Mobile Chat Toggle Button */}
        </div>
    );
}
